import { belongsToOkrTeam } from './okrTeamScope';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import OkrWeeklyBoard from './OkrWeeklyBoard';

const getId = (value) => value?._id || value?.id || value || '';
const sameId = (a, b) => String(getId(a)) === String(getId(b));

const getUserName = (user) => {
    if (!user) return '-';
    if (typeof user === 'string') return user;
    if (user.name || user.fullName) return user.name || user.fullName;
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name || user.email || '-';
};

const getPointOwnerId = (keyResult) => {
    return getId(keyResult?.ownerId || keyResult?.pointOwnerUserId || keyResult?.updateResponsibleUserId);
};

const OkrMyOkrs = ({
    selectedCycle,
    currentUser,
    teams = [],
    objectives = [],
    keyResults = [],
    weeklyEntries = [],
    users = [],
    scoreboard = [],
    currentWeek,
    selectedWeekNumber,
    onWeekChange,
    loading = false,
    canManage = false,
    onOpenEntry
}) => {
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [selectedOwnerId, setSelectedOwnerId] = useState('');
    const currentUserId = getId(currentUser);
    const selectedCycleId = getId(selectedCycle);
    const weeks = useMemo(() => (Array.isArray(selectedCycle?.weeks) ? selectedCycle.weeks : []), [selectedCycle]);
    const numberOfWeeks = Number(selectedCycle?.numberOfWeeks || weeks.length || 0);
    const currentWeekNumber = Number(currentWeek?.weekNumber || 0);

    const teamMap = useMemo(
        () => teams.reduce((map, team) => ({ ...map, [getId(team)]: team }), {}),
        [teams]
    );

    const objectiveMap = useMemo(
        () => objectives.reduce((map, objective) => ({ ...map, [getId(objective)]: objective }), {}),
        [objectives]
    );

    const fullReadTeamIds = useMemo(
        () => new Set(teams.filter((team) => getId(team?.managerId) === currentUserId || (team.readOnlyViewerIds || []).some(id => getId(id) === currentUserId)).map((team) => getId(team))),
        [currentUserId, teams]
    );

    const canViewWholeTeam = fullReadTeamIds.size > 0;
    const canViewMultiplePeople = canManage || canViewWholeTeam;

    const allMyKeyResults = useMemo(
        () =>
            keyResults
                .filter((keyResult) => {
                    if (keyResult?.isActive === false) return false;
                    if (selectedCycleId && !sameId(keyResult?.cycleId, selectedCycleId)) return false;
                    if (canManage) return true;
                    if (fullReadTeamIds.has(getId(keyResult?.teamId))) return true;
                    return getPointOwnerId(keyResult) === currentUserId;
                })
                .map((keyResult) => ({
                    ...keyResult,
                    team: teamMap[getId(keyResult.teamId)] || null,
                    objective: objectiveMap[getId(keyResult.objectiveId)] || null,
                    pointsPerWeek: numberOfWeeks > 0 ? Number(keyResult.weight || 0) / numberOfWeeks : 0
                })),
        [canManage, currentUserId, keyResults, fullReadTeamIds, numberOfWeeks, objectiveMap, selectedCycleId, teamMap]
    );

    const teamOptions = useMemo(() => {
        const options = (canManage
            ? teams
            : canViewWholeTeam
            ? teams.filter((team) => fullReadTeamIds.has(getId(team)) || allMyKeyResults.some((keyResult) => belongsToOkrTeam(keyResult, team)))
            : teams.filter((team) => allMyKeyResults.some((keyResult) => belongsToOkrTeam(keyResult, team)))
        ).map((team) => ({ label: team.name, value: getId(team) }));

        if (options.length > 1) {
            return [{ label: canManage || canViewWholeTeam ? 'Todos los equipos' : 'Todos mis equipos', value: '' }, ...options];
        }

        return options;
    }, [allMyKeyResults, canManage, canViewWholeTeam, fullReadTeamIds, teamMap, teams]);

    const ownerOptions = useMemo(() => {
        if (!canViewMultiplePeople) return [];

        const scopedKeyResults = allMyKeyResults.filter((keyResult) => !selectedTeamId || belongsToOkrTeam(keyResult, teamMap[selectedTeamId]));
        const ownerIds = [...new Set(scopedKeyResults.map((keyResult) => getPointOwnerId(keyResult)).filter(Boolean))];
        const findUser = (ownerId) => users.find((user) => getId(user) === ownerId);
        const options = ownerIds.map((ownerId) => ({
            label: getUserName(findUser(ownerId) || keyResults.find((keyResult) => getPointOwnerId(keyResult) === ownerId)?.ownerId || ownerId),
            value: ownerId
        }));

        if (options.length > 1) return [{ label: 'Todas las personas', value: '' }, ...options];
        return options;
    }, [allMyKeyResults, canViewMultiplePeople, keyResults, selectedTeamId, teamMap, users]);

    useEffect(() => {
        const concreteOptions = teamOptions.filter((option) => option.value);
        const isValidSelection = teamOptions.some((option) => option.value === selectedTeamId);

        if (concreteOptions.length === 1 && !selectedTeamId) {
            setSelectedTeamId(concreteOptions[0].value);
            return;
        }

        if (selectedTeamId && !isValidSelection) {
            setSelectedTeamId(concreteOptions.length === 1 ? concreteOptions[0].value : '');
        }
    }, [selectedTeamId, teamOptions]);

    useEffect(() => {
        if (!canViewMultiplePeople) {
            if (selectedOwnerId) setSelectedOwnerId('');
            return;
        }

        const concreteOptions = ownerOptions.filter((option) => option.value);
        const isValidSelection = ownerOptions.some((option) => option.value === selectedOwnerId);

        if (selectedOwnerId && !isValidSelection) {
            setSelectedOwnerId(concreteOptions.length === 1 ? concreteOptions[0].value : '');
        }
    }, [canViewMultiplePeople, ownerOptions, selectedOwnerId]);

    const weekOptions = useMemo(
        () =>
            weeks.map((item) => ({
                label: `${item.label || `Semana ${item.weekNumber}`}${Number(item.weekNumber) === currentWeekNumber ? ' - Semana actual' : ''}`,
                value: item.weekNumber
            })),
        [currentWeekNumber, weeks]
    );
    const selectedWeekOptionIndex = weekOptions.findIndex(
        (option) => Number(option.value) === Number(selectedWeekNumber)
    );
    const changeWeekBy = (amount) => {
        const fallbackIndex = selectedWeekOptionIndex >= 0 ? selectedWeekOptionIndex : 0;
        const nextOption = weekOptions[fallbackIndex + amount];
        if (nextOption) onWeekChange?.(nextOption.value);
    };

    const myKeyResults = useMemo(
        () =>
            allMyKeyResults.filter((keyResult) => {
                if (selectedTeamId && !belongsToOkrTeam(keyResult, teamMap[selectedTeamId])) return false;
                if (selectedOwnerId && getPointOwnerId(keyResult) !== selectedOwnerId) return false;
                return true;
            }),
        [allMyKeyResults, selectedOwnerId, selectedTeamId, teamMap]
    );

    const ownPointKeyResults = useMemo(
        () => {
            if (canViewMultiplePeople) return myKeyResults;
            return myKeyResults.filter((keyResult) => getPointOwnerId(keyResult) === currentUserId);
        },
        [canViewMultiplePeople, currentUserId, myKeyResults]
    );

    const myEntries = useMemo(() => {
        const keyResultIds = new Set(myKeyResults.map((keyResult) => getId(keyResult)));
        return weeklyEntries.filter((entry) => keyResultIds.has(getId(entry.keyResultId)) || (sameId(entry.ownerId, currentUserId) && keyResultIds.has(getId(entry.keyResultId))));
    }, [currentUserId, myKeyResults, weeklyEntries]);

    const openEntry = (keyResult, week, entry, forceLocked = false) => {
        if (!onOpenEntry) return;
        onOpenEntry({
            keyResult,
            team: keyResult.team,
            objective: keyResult.objective,
            entry,
            locked: forceLocked || Boolean(week?.isLocked),
            weekNumber: week?.weekNumber
        });
    };

    const rejectedRows = useMemo(
        () =>
            myEntries
                .filter((entry) => entry.status === 'rejected' && (!selectedWeekNumber || Number(entry.weekNumber) === Number(selectedWeekNumber)))
                .map((entry) => {
                    const keyResult = myKeyResults.find((item) => getId(item) === getId(entry.keyResultId));
                    const week = weeks.find((item) => Number(item.weekNumber) === Number(entry.weekNumber));
                    return { entry, keyResult, week };
                })
                .filter((row) => row.keyResult),
        [myEntries, myKeyResults, selectedWeekNumber, weeks]
    );


    if (!selectedCycle?._id) {
        return (
            <div className="surface-card border-round p-4 shadow-1 text-center">
                <i className="pi pi-user text-4xl text-primary mb-3" />
                <h3 className="mt-0 mb-2">Selecciona o crea un ciclo OKR.</h3>
                <p className="text-color-secondary m-0">Tus OKRs personales aparecerán cuando exista un ciclo seleccionado.</p>
            </div>
        );
    }

    const filterHeader = (
        <div className="surface-card border-round p-3 shadow-1 okr-my-filters">
            <div className="grid align-items-end">
                <div className={`col-12 ${canViewMultiplePeople ? 'md:col-4' : 'md:col-6'}`}>
                    <label className="block font-medium mb-2">Equipo</label>
                    <Dropdown
                        value={selectedTeamId}
                        options={teamOptions}
                        onChange={(event) => setSelectedTeamId(event.value || '')}
                        placeholder={canManage || canViewWholeTeam ? 'Todos los equipos' : 'Todos mis equipos'}
                        className="w-full"
                        disabled={!teamOptions.length}
                    />
                </div>
                {canViewMultiplePeople ? (
                    <div className="col-12 md:col-4">
                        <label className="block font-medium mb-2">Persona</label>
                        <Dropdown
                            value={selectedOwnerId}
                            options={ownerOptions}
                            onChange={(event) => setSelectedOwnerId(event.value || '')}
                            placeholder="Todas las personas"
                            className="w-full"
                            disabled={!ownerOptions.length}
                            filter
                        />
                    </div>
                ) : null}
                <div className={`col-12 ${canViewMultiplePeople ? 'md:col-4' : 'md:col-6'}`}>
                    <label className="block font-medium mb-2">Semana</label>
                    <div className="okr-week-selector">
                        <Button
                            type="button"
                            icon="pi pi-chevron-left"
                            className="p-button-outlined"
                            onClick={() => changeWeekBy(-1)}
                            disabled={!weekOptions.length || selectedWeekOptionIndex <= 0}
                            aria-label="Semana anterior"
                        />
                        <Dropdown
                            value={selectedWeekNumber}
                            options={weekOptions}
                            onChange={(event) => onWeekChange?.(event.value)}
                            placeholder="Semana actual"
                            className="w-full"
                            disabled={!weekOptions.length}
                            aria-label="Seleccionar semana"
                        />
                        <Button
                            type="button"
                            icon="pi pi-chevron-right"
                            className="p-button-outlined"
                            onClick={() => changeWeekBy(1)}
                            disabled={!weekOptions.length || selectedWeekOptionIndex < 0 || selectedWeekOptionIndex >= weekOptions.length - 1}
                            aria-label="Semana siguiente"
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    if (!allMyKeyResults.length && !loading) {
        return (
            <div className="flex flex-column gap-3">
                {filterHeader}
                <div className="surface-card border-round p-4 shadow-1 text-center">
                    <i className="pi pi-flag text-4xl text-primary mb-3" />
                    <h3 className="mt-0 mb-2">{canManage ? 'No hay resultados clave configurados en este ciclo.' : 'No tienes resultados clave asignados en este ciclo.'}</h3>
                    <p className="text-color-secondary m-0">
                        {canManage
                            ? 'Cuando existan resultados clave, podrás revisarlos aquí por equipo.'
                            : 'Cuando te asignen resultados clave, aparecerán aquí con tus avances y próximos pasos.'}
                    </p>
                </div>
            </div>
        );
    }

    if (selectedTeamId && !myKeyResults.length && !loading) {
        return (
            <div className="flex flex-column gap-3">
                {filterHeader}
                <div className="surface-card border-round p-4 shadow-1 text-center">
                    <i className="pi pi-filter text-4xl text-primary mb-3" />
                    <h3 className="mt-0 mb-2">No tienes resultados clave asignados para este equipo.</h3>
                    <p className="text-color-secondary m-0">Cambia el equipo para revisar otros OKRs personales del ciclo.</p>
                </div>
            </div>
        );
    }

    const rejectedActionBody = (rowData) => {
        const canEdit = canManage || getPointOwnerId(rowData.keyResult) === currentUserId;
        const locked = Boolean(rowData.week?.isLocked);

        return (
            <div className="flex align-items-center gap-2">
                <Button
                    label={canEdit && !locked ? 'Corregir' : 'Ver detalle'}
                    icon={canEdit && !locked ? 'pi pi-pencil' : 'pi pi-eye'}
                    className="p-button-sm p-button-outlined"
                    onClick={() => openEntry(rowData.keyResult, rowData.week, rowData.entry, !canEdit)}
                />
                {locked ? <Tag value="Bloqueada" severity="danger" /> : null}
            </div>
        );
    };

    return (
        <div className="flex flex-column gap-3 okr-my-okrs">
            {filterHeader}

            <div className="surface-card border-round p-3 shadow-1 okr-my-board-shell">
                {ownPointKeyResults.length ? (
                <OkrWeeklyBoard
                    selectedCycle={selectedCycle}
                    teams={teams}
                    objectives={objectives}
                    keyResults={ownPointKeyResults}
                    weeklyEntries={weeklyEntries}
                    users={users}
                    currentUser={currentUser}
                    canManage={canManage}
                    scoreboard={scoreboard}
                    teamFilter={selectedTeamId}
                    ownerFilter={selectedOwnerId}
                    selectedWeekNumber={selectedWeekNumber}
                    hideEmptyPeople
                    boardTitle="Mi tablero semanal"
                    boardDescription="Actualiza tus valores reales desde cada celda. Los puntos se calculan automáticamente al guardar."
                    emptyTitle={canViewMultiplePeople ? 'No hay resultados clave configurados para este equipo.' : 'No tienes resultados clave propios asignados en este ciclo.'}
                    emptyDescription="Cambia los filtros o revisa la configuración OKR del ciclo."
                    onWeekChange={onWeekChange}
                    onOpenEntry={onOpenEntry}
                />
                ) : (
                    <div className="surface-card border-round p-4 shadow-1 text-center">
                        <i className="pi pi-flag text-4xl text-primary mb-3" />
                        <h3 className="mt-0 mb-2">No tienes resultados clave propios asignados en este ciclo.</h3>
                        <p className="text-color-secondary m-0">Cuando seas la persona responsable de un resultado clave, aparecerá aquí.</p>
                    </div>
                )}
            </div>

            {rejectedRows.length ? (
                <div className="surface-card border-round p-3 shadow-1 okr-rejected-updates">
                    <h3 className="mt-0">Actualizaciones rechazadas</h3>
                    <DataTable value={rejectedRows} responsiveLayout="scroll" emptyMessage="No tienes actualizaciones rechazadas.">
                        <Column header="Semana" body={(rowData) => rowData.week?.label || `Semana ${rowData.entry?.weekNumber}`} style={{ minWidth: '120px' }} />
                        <Column header="Resultado clave" body={(rowData) => rowData.keyResult?.title || '-'} style={{ minWidth: '220px' }} />
                        <Column header="Equipo" body={(rowData) => rowData.keyResult?.team?.name || '-'} style={{ minWidth: '150px' }} />
                        <Column header="Motivo de rechazo" body={(rowData) => rowData.entry?.rejectionReason || '-'} style={{ minWidth: '240px' }} />
                        <Column header="Acción" body={rejectedActionBody} style={{ minWidth: '150px' }} />
                    </DataTable>
                </div>
            ) : null}
        </div>
    );
};

export default OkrMyOkrs;
